import UTIF from '@ctw/shared/vendor/utif';

export const tiffTypes = ['/tif', '/tiff'];
export const webImageTypes = [
  '/jpg',
  '/jpeg',
  '/png',
  '/apng',
  '/avif',
  '/gif',
  '/webp',
  '/svg+xml',
];

export const isSpecificContentType = (extensions: Array<string>, contentType: string) =>
  extensions.some((extension) => contentType.includes(extension));

// Decodes a TIFF buffer into a PNG data URL, returning `null` (rather than
// throwing) when the data is empty or malformed so callers can fall back gracefully.
export function tiffToPNG(
  arrayBuffer: ArrayBuffer,
  page = 0,
): {
  dataUrl: string;
  description: string;
  pageCount: number;
} | null {
  if (arrayBuffer.byteLength === 0) {
    return null;
  }

  try {
    const imageFileDirectory = UTIF.decode(arrayBuffer);
    const imageFileDirectoryPage = imageFileDirectory[page];
    // A renderable image page must carry width (t256) and height (t257) tags.
    const widthTag = imageFileDirectoryPage?.t256;
    const heightTag = imageFileDirectoryPage?.t257;
    if (!Array.isArray(widthTag) || widthTag.length === 0) {
      return null;
    }
    if (!Array.isArray(heightTag) || heightTag.length === 0) {
      return null;
    }
    // t270 is where an IFD indexes any description of the image
    if (!('t270' in imageFileDirectoryPage && Array.isArray(imageFileDirectoryPage.t270))) {
      imageFileDirectoryPage.t270 = ['']; // Set a default description
    }
    UTIF.decodeImage(arrayBuffer, imageFileDirectoryPage);
    const rgba = UTIF.toRGBA8(imageFileDirectoryPage); // Uint8Array with RGBA pixels

    const canvas = document.createElement('canvas');
    canvas.width = imageFileDirectoryPage.width;
    canvas.height = imageFileDirectoryPage.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    const img = ctx.createImageData(canvas.width, canvas.height);
    img.data.set(rgba);
    ctx.putImageData(img, 0, 0);

    return {
      dataUrl: canvas.toDataURL(),
      description: `${imageFileDirectoryPage.t270[0]}`,
      pageCount: imageFileDirectory.length,
    };
  } catch {
    return null;
  }
}

export function downloadBlob(blob: Blob, fileName?: string) {
  const fileUrl = URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.href = fileUrl;

  tempLink.download = fileName || '';
  document.body.appendChild(tempLink);

  tempLink.click();

  document.body.removeChild(tempLink);

  setTimeout(() => {
    URL.revokeObjectURL(fileUrl);
  }, 100);
}

/**
 * Renders the iframe configured by `configureIframe` offscreen and opens the
 * browser's print dialog once it has loaded. This is far more reliable than
 * `window.open()`, which — depending on content type and browser configuration —
 * downloads the file (always the case for TIFFs, which browsers can't render
 * natively, and often for PDFs) instead of previewing it, and whose `onload`
 * frequently never fires for the embedded PDF viewer.
 */
function printWithIframe(
  configureIframe: (iframe: HTMLIFrameElement) => void,
  onComplete?: () => void,
) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';

  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();

    // Clean up after the print dialog has had time to capture the content.
    setTimeout(() => {
      onComplete?.();
      iframe.remove();
    }, 1000);
  };

  configureIframe(iframe);
  document.body.appendChild(iframe);
}

export function printBlobUrl(blobUrl: string) {
  printWithIframe(
    (iframe) => {
      iframe.src = blobUrl;
    },
    () => URL.revokeObjectURL(blobUrl),
  );
}

/**
 * Prints a document's raw binary content directly, mirroring how it's rendered
 * in the viewer. PDFs and web images render natively in the print iframe; TIFFs
 * are first converted to PNG (as the renderer does) since browsers can't display
 * them natively.
 */
export function printBinaryDocument(blob: Blob, contentType: string) {
  if (isSpecificContentType(tiffTypes, contentType)) {
    void blob.arrayBuffer().then((arrayBuffer) => {
      const tiff = tiffToPNG(arrayBuffer);
      if (!tiff) {
        return;
      }
      const { dataUrl } = tiff;
      printWithIframe((iframe) => {
        iframe.srcdoc = `<!doctype html><html><head><style>@page{margin:0}body{margin:0}img{width:100%}</style></head><body><img src="${dataUrl}" alt=""></body></html>`;
      });
    });
    return;
  }

  printBlobUrl(URL.createObjectURL(blob));
}
